import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useNavigate } from "react-router-dom";
import baseUrl from "../../../../api/baseUrl";
import socket from "../../../../api/socket";
import {
  APPOINTMENT_STATUS_OPTIONS,
  doctorNameFromItem,
  FIELD_LABELS,
  formatAppointmentPurpose,
  formatQueueFromItem,
  isRoomAllocationEnabled,
  parseQueueInput,
  statusStyles,
  transferDoctorNamesFromItem,
} from "./appointmentViewShared";
import { isDoctorTransferEnabled } from "../../../../utils/clinicFeatures";
import { appointmentPatientId, openPatientSummaryFromAppointment } from "../../../../utils/openPatientSummaryInNewTab";
import { useOngoingStatusGate } from "./useOngoingStatusGate";

type AppointmentDetailsModalProps = {
  item: Record<string, any>;
  open: boolean;
  onClose: () => void;
  onStatusChange?: () => void;
  onOngoingStatus?: (patientId: string) => void;
};

function InfoChip({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-900/50 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
        {icon}
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
        {value}
      </div>
    </div>
  );
}

export default function AppointmentDetailsModal({
  item,
  open,
  onClose,
  onStatusChange,
  onOngoingStatus,
}: AppointmentDetailsModalProps) {
  const roomAllocationEnabled = isRoomAllocationEnabled();
  const doctorTransferEnabled = isDoctorTransferEnabled();
  const navigate = useNavigate();
  const [status, setStatus] = useState(item?.status ?? "");
  const [queueInput, setQueueInput] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [queueSaving, setQueueSaving] = useState(false);
  const [treatmentLoading, setTreatmentLoading] = useState(false);
  const { gateStatus, ongoingGateModal } = useOngoingStatusGate();

  useEffect(() => {
    if (!open) return;
    setStatus(item?.status ?? "");
    setQueueInput(formatQueueFromItem(item));
  }, [open, item?.status, item?.queue]);

  const patientName =
    item?.patientId?.fullName || item?.bookPatientId?.fullName || "N/A";
  const mobile =
    item?.patientId?.mobileNo ||
    item?.bookPatientId?.mobileNo ||
    item?.mobileNo ||
    "";
  const doctorName = doctorNameFromItem(item);
  const transferDoctorNames = transferDoctorNamesFromItem(item);
  const purpose = formatAppointmentPurpose(item);
  const patientId = appointmentPatientId(item);
  const hasPatient = Boolean(item?.patientId?.fullName && patientId);

  const emitAppointmentSync = useCallback(
    (statusVal: string, queueNum?: number) => {
      if (!item?._id) return;
      const payload: Record<string, unknown> = {
        appointmentid: item._id,
        status: statusVal,
        roomNumber: item?.roomNumber,
        sessionName: item?.sessionName,
      };
      if (roomAllocationEnabled && queueNum != null) {
        payload.queue = queueNum;
      } else if (item?.queue != null && item?.queue !== "") {
        payload.queue = item.queue;
      }
      socket.emit("allAppointmentStatus", payload);
      if (
        roomAllocationEnabled &&
        statusVal === "ongoing" &&
        queueNum != null
      ) {
        try {
          localStorage.setItem(
            "nextQueue",
            String(Math.max(0, Math.floor(queueNum)))
          );
        } catch {
          /* ignore */
        }
      }
    },
    [item, roomAllocationEnabled]
  );

  const handleStatusChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value;
      if (!next || !item?._id) return;

      const ok = await gateStatus(item, next);
      if (!ok) {
        e.target.value = status;
        return;
      }

      const queueNum = roomAllocationEnabled
        ? parseQueueInput(queueInput) ?? 0
        : undefined;

      setStatus(next);
      setStatusSaving(true);

      emitAppointmentSync(next, queueNum);

      if (next === "ongoing") {
        openPatientSummaryFromAppointment(
          item,
          roomAllocationEnabled ? queueNum : undefined
        );
      }

      baseUrl
        .patch(
          "/appointment/statusAppointmentById",
          {},
          { headers: { appointmentid: item._id, status: next } }
        )
        .then(() => onStatusChange?.())
        .catch((err) => {
          console.error(err);
          setStatus(item?.status ?? "");
        })
        .finally(() => setStatusSaving(false));
    },
    [
      item,
      onStatusChange,
      queueInput,
      emitAppointmentSync,
      roomAllocationEnabled,
      gateStatus,
      status,
    ]
  );

  const handleQueueSave = useCallback(() => {
    if (!item?._id) return;
    const parsed = parseQueueInput(queueInput);
    if (parsed === null) return;

    setQueueSaving(true);
    baseUrl
      .patch(
        "/appointment/queueAppointmentById",
        { queue: parsed },
        { headers: { appointmentid: item._id } }
      )
      .then(() => {
        setQueueInput(String(parsed));
        emitAppointmentSync(status || item?.status || "appointment", parsed);
        onStatusChange?.();
      })
      .catch((err) => console.error(err))
      .finally(() => setQueueSaving(false));
  }, [item, queueInput, status, emitAppointmentSync, onStatusChange]);

  const queueDisplay = (() => {
    const p = parseQueueInput(queueInput);
    if (p === null) return "—";
    return String(p);
  })();

  const handleAddTreatment = useCallback(() => {
    if (!patientId) return;
    setTreatmentLoading(true);
    baseUrl
      .post("treatment/create-treatment", { patientId })
      .then((response) => {
        onClose();
        navigate(
          `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}`
        );
      })
      .catch((err) => console.error(err))
      .finally(() => setTreatmentLoading(false));
  }, [patientId, navigate, onClose]);

  if (!open) {
    return <>{ongoingGateModal}</>;
  }

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="appointment-details-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close dialog"
      />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-600 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primaryLight px-5 py-4 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <EventIcon className="text-white" />
            </div>
            <div className="min-w-0">
              <h2
                id="appointment-details-title"
                className="text-lg font-bold text-white truncate"
              >
                Appointment
              </h2>
              <p className="text-xs text-white/80 truncate">
                {item?.date?.split("T")[0] ?? "—"}
                {item?.startTime && item?.endTime
                  ? ` · ${item.startTime}–${item.endTime}`
                  : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors shrink-0"
            aria-label="Close"
          >
            <CloseIcon className="text-white" sx={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Patient card */}
          <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-slate-800 border border-primary/20 p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-md">
                {patientName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <PersonIcon
                    className="text-primary shrink-0"
                    sx={{ fontSize: 18 }}
                  />
                  <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {patientName}
                  </span>
                </div>
                {mobile ? (
                  <a
                    href={`tel:${mobile.replace(/[\s\-()]/g, "")}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-primary transition-colors"
                    onClick={(e) => {
                      if (window.innerWidth >= 768) e.preventDefault();
                    }}
                  >
                    <LocalPhoneIcon sx={{ fontSize: 15 }} />
                    {mobile}
                  </a>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2">
                  {item?.type === "online" ? (
                    <span className="px-2 py-0.5 rounded-md text-xs font-medium capitalize bg-primary/15 text-primary">
                      online
                    </span>
                  ) : null}
                  {roomAllocationEnabled ? (
                    <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-200 tabular-nums">
                      Token #{queueDisplay}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <label
              htmlFor="appointment-modal-status"
              className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide"
            >
              Status
            </label>
            <select
              id="appointment-modal-status"
              value={status}
              onChange={handleStatusChange}
              disabled={statusSaving || !item?._id}
              className={`w-full px-4 py-2.5 rounded-xl text-sm font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/25 transition-colors disabled:opacity-60 ${
                statusStyles[status] || statusStyles.default
              }`}
            >
              <option value="">Select status</option>
              {APPOINTMENT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {statusSaving ? (
              <p className="text-xs text-slate-500">Updating status…</p>
            ) : null}
          </div>

          {roomAllocationEnabled ? (
            <div className="space-y-2">
              <label
                htmlFor="appointment-modal-queue"
                className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide"
              >
                {FIELD_LABELS.queue}
              </label>
              <div className="flex gap-2">
                <input
                  id="appointment-modal-queue"
                  type="number"
                  min={0}
                  step={1}
                  value={queueInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || /^\d+$/.test(v)) setQueueInput(v);
                  }}
                  disabled={queueSaving || !item?._id}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-primary/25 tabular-nums"
                  placeholder="0"
                />
                <button
                  type="button"
                  onClick={handleQueueSave}
                  disabled={queueSaving || !item?._id}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-40 transition-opacity shrink-0"
                >
                  {queueSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : null}

          {/* Quick info grid */}
          <div className="grid grid-cols-2 gap-2">
            <InfoChip
              icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
              label="Date"
              value={item?.date?.split("T")[0] ?? "—"}
            />
            <InfoChip
              icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
              label="Time"
              value={
                item?.startTime && item?.endTime
                  ? `${item.startTime} – ${item.endTime}`
                  : "—"
              }
            />
            <InfoChip
              icon={<MeetingRoomIcon sx={{ fontSize: 14 }} />}
              label="Room"
              value={String(item?.roomNumber ?? "").trim() || "—"}
            />
            {roomAllocationEnabled ? (
              <InfoChip
                icon={<FormatListNumberedIcon sx={{ fontSize: 14 }} />}
                label="Token"
                value={queueDisplay}
              />
            ) : null}
          </div>

          {/* Details list */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-600 divide-y divide-slate-100 dark:divide-slate-700">
            <div className="px-4 py-3 flex justify-between gap-3 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Doctor</span>
              <span className="font-medium text-slate-800 dark:text-slate-100 text-right">
                {doctorName}
              </span>
            </div>
            {doctorTransferEnabled && transferDoctorNames ? (
              <div className="px-4 py-3 flex justify-between gap-3 text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                  Transferred to
                </span>
                <span className="font-medium text-sky-700 dark:text-sky-300 text-right">
                  {transferDoctorNames}
                </span>
              </div>
            ) : null}
            <div className="px-4 py-3 flex justify-between gap-3 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Session</span>
              <span className="font-medium text-slate-800 dark:text-slate-100 text-right">
                {item?.sessionName || "—"}
              </span>
            </div>
            <div className="px-4 py-3 flex justify-between gap-3 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Purpose</span>
              <span className="font-medium text-slate-800 dark:text-slate-100 text-right">
                {purpose}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="shrink-0 px-5 py-4 border-t border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-900/50 flex gap-2">
          {hasPatient ? (
            <button
              type="button"
              onClick={handleAddTreatment}
              disabled={treatmentLoading}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              <MedicalInformationIcon sx={{ fontSize: 18 }} />
              {treatmentLoading ? "Loading…" : "Add treatment"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors ${
              hasPatient ? "flex-1" : "w-full"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
    {ongoingGateModal}
    </>
  );
}
